import {
  ContactFormActionsContext,
  ContactFormDataContext,
  useContactFormActionsFromContext,
  useContactFormDataFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import React, { useMemo } from 'react';
import { ICONS, USER_TYPES } from 'src/common/constants';
import { Heading } from 'src/components/Form';
import Row from 'src/components/Row';
import Col from 'src/components/Col';
import Tile from 'src/components/Tile';
import { variables } from 'src/Tenants';
import IcoMoon from 'src/components/IcoMoon';
import '../../styles/icomoon/style.scss';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';

interface RoleTileSelectorProps {
  isFromPropertyOrSuite?: boolean;
  allowedRoles?: string[];
  formType?: string;
  selectedContact?: IContact | null;
  editingExistingContactInTable?: boolean;
}

const RoleTileSelector = ({
  isFromPropertyOrSuite,
  allowedRoles,
  formType,
  selectedContact,
  editingExistingContactInTable,
}: RoleTileSelectorProps) => {
  const { contactFormData, errors }: ContactFormDataContext =
    useContactFormDataFromContext();
  const { setContactFormData }: ContactFormActionsContext =
    useContactFormActionsFromContext();

  if (isFromPropertyOrSuite && !selectedContact && formType !== 'New') {
    return null;
  }

  const userTypeForExistingContact: string[] = useMemo(() => {
    return USER_TYPES.filter(
      (userType: string) => userType === contactFormData.userRole
    );
  }, [contactFormData.userRole]);

  const handleRoleSelection = (userType: string) => {
    if (userType === contactFormData.userRole) {
      return;
    }

    /* TODO: HACK - Preserve property data when coming from Property Developer page
     * to prevent losing the pre-selected property. Root cause: role change should
     * not clear all form data, but refactoring the entire form state management
     * is out of scope right now. Or figuring out why some properties don't align on the API etc.*/
    const propertyData = isFromPropertyOrSuite
      ? {
          properties: contactFormData.properties,
          propertyId: contactFormData.propertyId,
        }
      : {
          propertyId: '',
          properties: undefined,
        };

    // NEW: Preserve suite data when coming from Suites page.
    const suiteData = isFromPropertyOrSuite
      ? {
          suiteId: contactFormData.suiteId,
          Suites: contactFormData.Suites,
        }
      : {
          suiteId: '',
          Suites: [],
        };

    setContactFormData({
      userRole: userType,
      ...propertyData,
      ...suiteData,
      serviceType: '',
      servicesProvided:
        contactFormData.userRole === userType
          ? contactFormData.servicesProvided
          : [],
      isPrimaryContact: false,
    });
  };

  const isExistingAccount: boolean = isFromPropertyOrSuite
    ? !!contactFormData?.id && formType !== 'New' && isFromPropertyOrSuite
    : !!contactFormData?.id;

  const userTypesToShow: string[] = useMemo(() => {
    if (isExistingAccount) {
      return userTypeForExistingContact;
    }

    // For new contacts, filter by allowedRoles if provided.
    if (allowedRoles && allowedRoles.length > 0) {
      return USER_TYPES.filter((type) => allowedRoles.includes(type));
    }

    // Default: show all roles.
    return USER_TYPES;
  }, [isExistingAccount, userTypeForExistingContact, allowedRoles]);

  return (
    <>
      <div className="input__error">{errors.userRole}</div>
      <Heading>Role</Heading>
      {isExistingAccount ||
        (editingExistingContactInTable && (
          <div
            style={{
              color: '#d0285b',
              marginBottom: '2rem',
              fontSize: '1.5rem',
              fontStyle: 'italic',
            }}
          >
            This cannot be modified after a user is created. If you need to
            change it, please create a new contact or contact Juniper support.
          </div>
        ))}
      <div style={{ pointerEvents: contactFormData?.id ? 'none' : undefined }}>
        <Row granola>
          {userTypesToShow.map((userType: string) => (
            <Col granola key={userType} xs="6" md="4">
              <Tile
                title={
                  userType === 'Property Developer'
                    ? variables.propertyDeveloper
                    : userType === 'Home Owner'
                      ? variables.homeOwner
                      : userType
                }
                size="small"
                active={userType === contactFormData.userRole}
                onClick={() => handleRoleSelection(userType)}
                icon={<IcoMoon glyph={ICONS[userType]} />}
              />
            </Col>
          ))}
        </Row>
      </div>
    </>
  );
};

export default RoleTileSelector;
